// import CacheService from '../cache/CacheService'
import { keyActionMapping } from 'base/store/actions'
import { store } from '../store'
import { PERSISTENCE_KEY } from '../store/store'
import { ApiRequestService, ApiUrls } from './ApiRequestService'
import { EventService } from './EventService'

class PullServiceProvider {
  protected readonly pullInterval = 60000
  protected readonly selfLocalstorageKey = 'pull_service'
  protected $timer = 0

  public initialised = false

  protected getItemFromLocalStorage(key: string): any {
    const value = localStorage.getItem(key)

    if (!value) return {}

    return JSON.parse(value)
  }

  protected setItemFromLocalStorage(key: string, payload: any): any {
    localStorage.setItem(this.selfLocalstorageKey, JSON.stringify(payload))
  }

  protected getTimestampFromLocalStorage(key: string): number {
    const item = this.getItemFromLocalStorage(PERSISTENCE_KEY)[key]

    if (!item) return 0

    return item.ts || 0
  }

  protected buildTimestampParams() {
    const tsUser = this.getTimestampFromLocalStorage('user')
    const tsStatus = this.getTimestampFromLocalStorage('status')
    const tsCluster = this.getTimestampFromLocalStorage('cluster')
    const tsMonitor = this.getTimestampFromLocalStorage('monitor')
    const tsLocalmonitor = this.getTimestampFromLocalStorage('localmonitor')
    const tsNews = this.getTimestampFromLocalStorage('news')
    const tsEvent = this.getTimestampFromLocalStorage('event')
    const tsAlarm = this.getTimestampFromLocalStorage('alarm')
    const tsDirectMessage = this.getTimestampFromLocalStorage('dm')
    const tsMessage = this.getTimestampFromLocalStorage('message')
    const tsMessageChannel = this.getTimestampFromLocalStorage('message_channel')
    const tsStatusplan = this.getTimestampFromLocalStorage('statusplan')
    return `ts_user=${tsUser}&ts_status=${tsStatus}&ts_cluster=${tsCluster}&ts_monitor=${tsMonitor}` +
           `&ts_localmonitor=${tsLocalmonitor}&ts_news=${tsNews}&ts_event=${tsEvent}&ts_alarm=${tsAlarm}` +
           `&ts_direct_message=${tsDirectMessage}&ts_message=${tsMessage}&ts_message_channel=${tsMessageChannel}` +
           `&ts_statusplan=${tsStatusplan}`
  }

  protected async persist(key: keyof typeof keyActionMapping, data: any, unmodifiedData?: any) {
    void store.dispatch(keyActionMapping[key], data)

    EventService.$emit('pullservice--validate-cache-' + key, unmodifiedData ? unmodifiedData : data)
  }

  protected canPersistOnly(key: string, data: any): boolean {
    // MESSAGE doesn't have a sorting, but still can't be written to the storage as is
    return !data.sorting && (key !== 'message')
  }

  protected prepareData(key: string, storedData: any, newData: any) {
    switch (key) {
    // MESSAGE has a different data structure that shouldn't be written to the storage directly
    case 'message':
      storedData.limit = newData.limit
      return storedData
    default:
      return storedData
    }
  }

  protected updateState(key: string, data: any) {
    if (this.canPersistOnly(key, data)) {
      void this.persist(key, data)

      return
    }

    const storageData = this.prepareData(key, this.getItemFromLocalStorage(key), data)

    // update timestamp
    storageData.ts = data.ts

    if (storageData.items === undefined) {
      storageData.items = {}
    }

    // add new items
    for (let id in data.items) {
      if (!data.items.hasOwnProperty(id)) continue
      storageData.items[id] = data.items[id]
    }

    // remove items that are not listed in `sorting`
    if (data.sorting) {
      for (let id in storageData.items) {
        if (
          !storageData.items.hasOwnProperty(id) ||
          (data.sorting.indexOf(parseInt(id, 10)) !== -1)
        ) {
          continue
        }

        delete storageData.items[id]
      }

      storageData.sorting = [...data.sorting]
    }

    void this.persist(key, storageData, data)
  }

  protected processReply(replyData: any) {
    const validKeys = [
      'user', 'status', 'cluster', 'monitor', 'alarm', 'news',
      'events', 'dm', 'message_channel', 'message', 'localmonitor',
      'statusplan', 'ucr', 'ucr_default', 'ucr_active'
    ]

    let i
    for (i in validKeys) {
      const key = validKeys[i]

      if (typeof replyData[key] === 'undefined') continue

      const data = replyData[key]
      // const localKey = this.convertApiKeyToLocalStorageKey(key)

      this.updateState(key, data)
    }
  }

  public async pull() {
    const params = this.buildTimestampParams()
    const reply = await ApiRequestService.get(ApiUrls.pull.all + '?' + params)

    if (!reply || (reply.status !== 200) || !reply.data || !reply.data.success || !reply.data.data) {
      console.error('PullServiceProvider.pull()', 'api request failed')
      return
    }

    const selfData = this.getItemFromLocalStorage(this.selfLocalstorageKey)
    selfData.ts = reply.data.data.ts
    this.setItemFromLocalStorage(this.selfLocalstorageKey, selfData)

    this.processReply(reply.data.data)
  }

  protected async onTimer() {
    await this.pull()
  }

  async start() {
    await this.onTimer()
    this.$timer = window.setInterval(this.onTimer.bind(this), this.pullInterval)
    this.initialised = true
  }

  stop() {
    this.initialised = false
    if (!this.$timer) return

    window.clearInterval(this.$timer)
    this.$timer = 0
  }
}

export const PullService = new PullServiceProvider()
